import React, { useState, useEffect } from 'react';
import { apiClient } from '../api/client';
import { VIEW_CATEGORIES, NAV_ITEMS } from '../config/navConfig';
import {
  PRIMARY,
  TEXT_DARK,
  TEXT_MUTED,
  BORDER_LIGHT,
  BG_SURFACE,
} from './legacy-ball/table-management/designTokens';

// ── Types ──────────────────────────────────────────────────────

interface FocusedNavConfig {
  enabled: boolean;
  activateAt: string | null;
  deactivateAt: string | null;
  currentStatus: 'active' | 'inactive' | 'scheduled';
}

interface GroupWithAssignmentCount {
  groupType: 'SubCommittee' | 'EventGroup';
  groupId: string;
  name: string;
  assignedRouteCount: number;
}

interface GroupAssignmentResponse {
  routes: string[];
}

// ── Component ──────────────────────────────────────────────────

export function FocusedNavConfigPanel() {
  const [config, setConfig] = useState<FocusedNavConfig>({
    enabled: false,
    activateAt: null,
    deactivateAt: null,
    currentStatus: 'inactive',
  });
  const [groups, setGroups] = useState<GroupWithAssignmentCount[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  // Local form state
  const [enabled, setEnabled] = useState(false);
  const [activateAt, setActivateAt] = useState('');
  const [deactivateAt, setDeactivateAt] = useState('');

  // Route assignment state
  const [expandedGroup, setExpandedGroup] = useState<string | null>(null);
  const [assignedRoutes, setAssignedRoutes] = useState<string[]>([]);
  const [loadingRoutes, setLoadingRoutes] = useState(false);
  const [savingRoutes, setSavingRoutes] = useState(false);
  const [routesSaved, setRoutesSaved] = useState(false);
  const [routesError, setRoutesError] = useState('');

  const fetchGroups = async () => {
    try {
      const groupsData = await apiClient<GroupWithAssignmentCount[]>('/focused-nav/groups');
      setGroups(groupsData);
    } catch {
      // Silently fail; error already shown if initial load failed
    }
  };

  useEffect(() => {
    async function fetchData() {
      try {
        const [configData, groupsData] = await Promise.all([
          apiClient<FocusedNavConfig>('/focused-nav/config'),
          apiClient<GroupWithAssignmentCount[]>('/focused-nav/groups'),
        ]);
        setConfig(configData);
        setEnabled(configData.enabled);
        setActivateAt(configData.activateAt ? configData.activateAt.slice(0, 16) : '');
        setDeactivateAt(configData.deactivateAt ? configData.deactivateAt.slice(0, 16) : '');
        setGroups(groupsData);
      } catch {
        setError('Failed to load focused navigation configuration');
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSaved(false);
    try {
      const updated = await apiClient<FocusedNavConfig>('/focused-nav/config', {
        method: 'PUT',
        body: {
          enabled,
          activateAt: activateAt || null,
          deactivateAt: deactivateAt || null,
        },
      });
      setConfig(updated);
      setEnabled(updated.enabled);
      setActivateAt(updated.activateAt ? updated.activateAt.slice(0, 16) : '');
      setDeactivateAt(updated.deactivateAt ? updated.deactivateAt.slice(0, 16) : '');
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch {
      setError('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleGroupClick = async (group: GroupWithAssignmentCount) => {
    const groupKey = `${group.groupType}-${group.groupId}`;

    if (expandedGroup === groupKey) {
      // Collapse if already expanded
      setExpandedGroup(null);
      setAssignedRoutes([]);
      setRoutesError('');
      return;
    }

    setExpandedGroup(groupKey);
    setLoadingRoutes(true);
    setRoutesError('');
    setRoutesSaved(false);

    try {
      const data = await apiClient<GroupAssignmentResponse>(
        `/focused-nav/assignments/${group.groupType}/${group.groupId}`,
      );
      setAssignedRoutes(data.routes || []);
    } catch {
      setRoutesError('Failed to load route assignments');
      setAssignedRoutes([]);
    } finally {
      setLoadingRoutes(false);
    }
  };

  const handleRouteToggle = (route: string) => {
    setAssignedRoutes((prev) =>
      prev.includes(route) ? prev.filter((r) => r !== route) : [...prev, route],
    );
    setRoutesSaved(false);
  };

  const handleSaveRoutes = async () => {
    if (!expandedGroup) return;

    const [groupType, groupId] = [
      expandedGroup.split('-')[0],
      expandedGroup.substring(expandedGroup.indexOf('-') + 1),
    ];

    setSavingRoutes(true);
    setRoutesError('');
    setRoutesSaved(false);

    try {
      await apiClient(`/focused-nav/assignments/${groupType}/${groupId}`, {
        method: 'PUT',
        body: { routes: assignedRoutes },
      });
      setRoutesSaved(true);
      setTimeout(() => setRoutesSaved(false), 3000);
      // Refresh groups list to update the route count
      await fetchGroups();
    } catch {
      setRoutesError('Failed to save route assignments');
    } finally {
      setSavingRoutes(false);
    }
  };

  // Determine if warning should show: enabling with no groups that have assignments
  const hasConfiguredGroups = groups.some((g) => g.assignedRouteCount > 0);
  const showWarning = enabled && !hasConfiguredGroups;

  if (loading) {
    return (
      <div style={panelStyles.loading}>Loading focused navigation settings...</div>
    );
  }

  // Group NAV_ITEMS by ViewCategory for the route checklist
  const routesByCategory = VIEW_CATEGORIES.map((cat) => ({
    category: cat,
    items: NAV_ITEMS.filter((item) => item.viewId === cat.id),
  })).filter((group) => group.items.length > 0);

  return (
    <div style={panelStyles.container}>
      {/* Status Indicator */}
      <div style={panelStyles.statusRow}>
        <span style={panelStyles.statusLabel}>Current Status:</span>
        <span style={getStatusBadgeStyle(config.currentStatus)}>
          {config.currentStatus === 'active' && '● Active'}
          {config.currentStatus === 'inactive' && '● Inactive'}
          {config.currentStatus === 'scheduled' && '● Scheduled'}
        </span>
      </div>

      {/* Toggle Switch for Enabled */}
      <div style={panelStyles.fieldRow}>
        <label style={panelStyles.fieldLabel}>Enable Focused Navigation</label>
        <button
          onClick={() => setEnabled(!enabled)}
          style={{
            ...panelStyles.toggle,
            backgroundColor: enabled ? PRIMARY : '#ccc',
          }}
          aria-pressed={enabled}
          aria-label="Toggle focused navigation mode"
        >
          <span
            style={{
              ...panelStyles.toggleKnob,
              left: enabled ? '25px' : '3px',
            }}
          />
        </button>
        <span style={panelStyles.toggleHint}>
          {enabled ? 'Focused mode enabled' : 'Focused mode disabled'}
        </span>
      </div>

      {/* Warning Banner */}
      {showWarning && (
        <div style={panelStyles.warningBanner} role="alert">
          ⚠️ No groups have route assignments configured. Focused navigation will have no effect until at least one group has assigned routes.
        </div>
      )}

      {/* Date Pickers */}
      <div style={panelStyles.dateSection}>
        <div style={panelStyles.dateField}>
          <label style={panelStyles.fieldLabel}>Activate At</label>
          <input
            type="datetime-local"
            value={activateAt}
            onChange={(e) => setActivateAt(e.target.value)}
            style={panelStyles.dateInput}
            aria-label="Activation start date"
          />
          <p style={panelStyles.fieldHint}>
            Optional. When set, focused mode activates at this date/time.
          </p>
        </div>
        <div style={panelStyles.dateField}>
          <label style={panelStyles.fieldLabel}>Deactivate At</label>
          <input
            type="datetime-local"
            value={deactivateAt}
            onChange={(e) => setDeactivateAt(e.target.value)}
            style={panelStyles.dateInput}
            aria-label="Activation end date"
          />
          <p style={panelStyles.fieldHint}>
            Optional. When set, focused mode deactivates at this date/time.
          </p>
        </div>
      </div>

      {/* Save Button */}
      <div style={panelStyles.saveRow}>
        <button
          onClick={handleSave}
          disabled={saving}
          style={{
            ...panelStyles.saveBtn,
            cursor: saving ? 'not-allowed' : 'pointer',
            opacity: saving ? 0.7 : 1,
          }}
        >
          {saving ? 'Saving...' : 'Save Configuration'}
        </button>
        {saved && <span style={panelStyles.savedMsg}>✓ Saved</span>}
        {error && <span style={panelStyles.errorMsg}>{error}</span>}
      </div>

      {/* Groups List with Route Assignment */}
      <div style={panelStyles.groupsSection}>
        <h4 style={panelStyles.groupsHeading}>Group Route Assignments</h4>
        <p style={panelStyles.groupsDesc}>
          Click a group to expand and assign page routes. Each group's members will only see their assigned pages during focused mode.
        </p>
        {groups.length === 0 ? (
          <p style={panelStyles.emptyMsg}>No groups found.</p>
        ) : (
          <div style={panelStyles.groupsList}>
            {groups.map((group) => {
              const groupKey = `${group.groupType}-${group.groupId}`;
              const isExpanded = expandedGroup === groupKey;

              return (
                <div key={groupKey}>
                  <button
                    onClick={() => handleGroupClick(group)}
                    style={{
                      ...panelStyles.groupItem,
                      borderColor: isExpanded ? PRIMARY : BORDER_LIGHT,
                      cursor: 'pointer',
                      width: '100%',
                      textAlign: 'left' as const,
                    }}
                    aria-expanded={isExpanded}
                    aria-label={`${group.name} - ${group.assignedRouteCount} routes assigned`}
                  >
                    <div style={panelStyles.groupInfo}>
                      <span style={panelStyles.groupName}>{group.name}</span>
                      <span style={panelStyles.groupType}>
                        {group.groupType === 'SubCommittee' ? 'Subcommittee' : 'Event Group'}
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={panelStyles.routeCount}>
                        {group.assignedRouteCount} {group.assignedRouteCount === 1 ? 'route' : 'routes'}
                      </span>
                      <span style={{ fontSize: '0.75rem', color: TEXT_MUTED }}>
                        {isExpanded ? '▲' : '▼'}
                      </span>
                    </div>
                  </button>

                  {/* Expanded Route Checklist */}
                  {isExpanded && (
                    <div style={panelStyles.routePanel}>
                      {loadingRoutes ? (
                        <p style={panelStyles.routeLoading}>Loading routes...</p>
                      ) : (
                        <>
                          {routesByCategory.map((catGroup) => (
                            <div key={catGroup.category.id} style={panelStyles.routeCategory}>
                              <div style={panelStyles.routeCategoryHeader}>
                                <span>{catGroup.category.icon}</span>
                                <span style={panelStyles.routeCategoryLabel}>
                                  {catGroup.category.label}
                                </span>
                              </div>
                              <div style={panelStyles.routeChecklistItems}>
                                {catGroup.items.map((item) => (
                                  <label
                                    key={item.path}
                                    style={panelStyles.routeCheckItem}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={assignedRoutes.includes(item.path)}
                                      onChange={() => handleRouteToggle(item.path)}
                                      style={panelStyles.routeCheckbox}
                                      aria-label={`Assign ${item.label} route`}
                                    />
                                    <span style={panelStyles.routeItemIcon}>{item.icon}</span>
                                    <span style={panelStyles.routeItemLabel}>{item.label}</span>
                                    <span style={panelStyles.routeItemPath}>{item.path}</span>
                                  </label>
                                ))}
                              </div>
                            </div>
                          ))}

                          {/* Save Routes Button */}
                          <div style={panelStyles.routeSaveRow}>
                            <button
                              onClick={handleSaveRoutes}
                              disabled={savingRoutes}
                              style={{
                                ...panelStyles.routeSaveBtn,
                                cursor: savingRoutes ? 'not-allowed' : 'pointer',
                                opacity: savingRoutes ? 0.7 : 1,
                              }}
                            >
                              {savingRoutes ? 'Saving...' : 'Save Routes'}
                            </button>
                            {routesSaved && (
                              <span style={panelStyles.savedMsg}>✓ Routes saved</span>
                            )}
                            {routesError && (
                              <span style={panelStyles.errorMsg}>{routesError}</span>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Helpers ────────────────────────────────────────────────────

function getStatusBadgeStyle(status: 'active' | 'inactive' | 'scheduled'): React.CSSProperties {
  const colors = {
    active: { bg: '#e8f5e9', text: '#2e7d32' },
    inactive: { bg: '#f5f5f5', text: '#757575' },
    scheduled: { bg: '#e3f2fd', text: '#1565c0' },
  };
  const c = colors[status];
  return {
    display: 'inline-block',
    padding: '4px 10px',
    borderRadius: '12px',
    fontSize: '0.8rem',
    fontWeight: 600,
    backgroundColor: c.bg,
    color: c.text,
  };
}

// ── Styles ─────────────────────────────────────────────────────

const panelStyles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1.25rem',
    padding: '1rem 1.25rem',
  },
  loading: {
    padding: '1rem 1.25rem',
    fontSize: '0.85rem',
    color: TEXT_MUTED,
  },
  statusRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
  },
  statusLabel: {
    fontSize: '0.85rem',
    fontWeight: 600,
    color: TEXT_DARK,
  },
  fieldRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
  },
  fieldLabel: {
    fontSize: '0.85rem',
    fontWeight: 600,
    color: '#333',
    display: 'block',
    marginBottom: '4px',
  },
  toggle: {
    width: '48px',
    height: '26px',
    borderRadius: '13px',
    border: 'none',
    position: 'relative' as const,
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  },
  toggleKnob: {
    position: 'absolute' as const,
    top: '3px',
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    backgroundColor: '#fff',
    transition: 'left 0.2s',
    boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
  },
  toggleHint: {
    fontSize: '0.8rem',
    color: '#666',
  },
  warningBanner: {
    padding: '10px 14px',
    backgroundColor: '#fff8e1',
    border: '1px solid #ffe082',
    borderRadius: '6px',
    fontSize: '0.85rem',
    color: '#f57f17',
    lineHeight: 1.4,
  },
  dateSection: {
    display: 'flex',
    gap: '1rem',
    flexWrap: 'wrap' as const,
  },
  dateField: {
    flex: '1 1 200px',
  },
  dateInput: {
    width: '100%',
    padding: '8px 12px',
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '6px',
    fontSize: '0.9rem',
    boxSizing: 'border-box' as const,
  },
  fieldHint: {
    fontSize: '0.75rem',
    color: '#999',
    margin: '4px 0 0 0',
  },
  saveRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
  },
  saveBtn: {
    padding: '10px 20px',
    backgroundColor: PRIMARY,
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: 600,
  },
  savedMsg: {
    fontSize: '0.8rem',
    color: '#2e7d32',
  },
  errorMsg: {
    fontSize: '0.8rem',
    color: '#d32f2f',
  },
  groupsSection: {
    borderTop: `1px solid ${BORDER_LIGHT}`,
    paddingTop: '1rem',
  },
  groupsHeading: {
    fontSize: '0.95rem',
    fontWeight: 600,
    color: TEXT_DARK,
    margin: '0 0 0.25rem 0',
  },
  groupsDesc: {
    fontSize: '0.8rem',
    color: TEXT_MUTED,
    margin: '0 0 0.75rem 0',
  },
  emptyMsg: {
    fontSize: '0.85rem',
    color: TEXT_MUTED,
    fontStyle: 'italic',
  },
  groupsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
  },
  groupItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 14px',
    backgroundColor: BG_SURFACE,
    borderRadius: '6px',
    border: `1px solid ${BORDER_LIGHT}`,
    transition: 'border-color 0.15s',
  },
  groupInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  groupName: {
    fontSize: '0.9rem',
    fontWeight: 500,
    color: TEXT_DARK,
  },
  groupType: {
    fontSize: '0.75rem',
    color: TEXT_MUTED,
  },
  routeCount: {
    fontSize: '0.8rem',
    fontWeight: 600,
    color: PRIMARY,
    whiteSpace: 'nowrap' as const,
  },
  // Route assignment panel styles
  routePanel: {
    padding: '12px 14px',
    backgroundColor: '#fafbfc',
    border: `1px solid ${BORDER_LIGHT}`,
    borderTop: 'none',
    borderRadius: '0 0 6px 6px',
    marginTop: '-4px',
  },
  routeLoading: {
    fontSize: '0.8rem',
    color: TEXT_MUTED,
    fontStyle: 'italic',
    margin: 0,
  },
  routeCategory: {
    marginBottom: '12px',
  },
  routeCategoryHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    marginBottom: '6px',
    paddingBottom: '4px',
    borderBottom: `1px solid ${BORDER_LIGHT}`,
  },
  routeCategoryLabel: {
    fontSize: '0.8rem',
    fontWeight: 600,
    color: TEXT_DARK,
  },
  routeChecklistItems: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    paddingLeft: '4px',
  },
  routeCheckItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '4px 6px',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.8rem',
    transition: 'background-color 0.1s',
  },
  routeCheckbox: {
    width: '14px',
    height: '14px',
    cursor: 'pointer',
    accentColor: PRIMARY,
  },
  routeItemIcon: {
    fontSize: '0.85rem',
  },
  routeItemLabel: {
    fontSize: '0.8rem',
    color: TEXT_DARK,
    fontWeight: 500,
  },
  routeItemPath: {
    fontSize: '0.7rem',
    color: TEXT_MUTED,
    marginLeft: 'auto',
  },
  routeSaveRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    marginTop: '12px',
    paddingTop: '10px',
    borderTop: `1px solid ${BORDER_LIGHT}`,
  },
  routeSaveBtn: {
    padding: '8px 16px',
    backgroundColor: PRIMARY,
    color: '#fff',
    border: 'none',
    borderRadius: '5px',
    fontSize: '12px',
    fontWeight: 600,
  },
};
